import { Badge, Box, Button, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PageTitle } from '../../../components/PageTitle'
import { SectionCard } from '../../../components/SectionCard'
import { Tooltip } from '../../../components/Tooltip'
import { useHasRole, useAppToast } from '../../../helpers'
import { ESupportTicketAssignee, ESupportTicketStatus, ISupportTicket } from '../../../services/Support/interfaces'
import { SupportService } from '../../../services/Support/SupportService'
import FinancialIndicatorsSection from '../FinancialIndicators/components/FinancialIndicatorsSection'

/**
 * Format date to "time ago" format (e.g., "5min atrás", "2h atrás", "3 dias atrás")
 *
 * @param dateStr - ISO date string to format
 * @returns Formatted time ago string
 */
function formatTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const minutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)
  if (minutes < 1) return 'Agora mesmo'
  if (minutes < 60) return `${minutes}min atrás`
  if (hours < 24) return `${hours}h atrás`
  if (days < 30) return days === 1 ? '1 dia atrás' : `${days} dias atrás`
  const months = Math.floor(days / 30)
  return months === 1 ? '1 mês atrás' : `${months} meses atrás`
}

export default function HomePage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const hasRole = useHasRole()
  const canViewFinancialIndicators = hasRole('view_financial_indicators')
  const supportService = new SupportService()
  const [supportTickets, setSupportTickets] = useState<ISupportTicket[]>([])

  useEffect(() => {
    loadSupportTickets()
  }, [])

  const loadSupportTickets = async () => {
    try {
      const data = await supportService.getRecentTickets()
      setSupportTickets(data.supportTickets)
    } catch (error) {
      toast.error('Não foi possível carregar os chamados de suporte')
    }
  }

  return (
    <>
      <PageTitle title="Dashboard" description="Resumo do que precisa da sua atenção hoje" />

      {/* Support Tickets Section */}
      <SectionCard
        variant="flush"
        title="Meus chamados"
        actions={
          <Button size="xs" variant="outline" colorScheme="magenta" onClick={() => navigate('/support')}>
            Ir para a tela de chamados
          </Button>
        }
      >
        <Box p={4}>
          {supportTickets.length === 0 ? (
            <Text variant="caption" textAlign="center">
              Nenhum chamado para mostrar aqui, ao abrir um chamado ele aparecerá aqui
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              {supportTickets.map((ticket) => (
                <SectionCard
                  key={ticket.id}
                  variant="tile"
                  cursor="pointer"
                  _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                  transition="all 0.15s"
                  onClick={() => navigate(`/support/${ticket.id}`)}
                  display="flex"
                  flexDirection="column"
                >
                  <Flex justify="space-between" align="flex-start" mb={2} gap={2}>
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={2} flex={1}>
                      {ticket.resume}
                    </Text>
                    <Badge
                      colorScheme={ticket.status === ESupportTicketStatus.OPEN ? 'yellow' : 'green'}
                      flexShrink={0}
                    >
                      {ticket.status === ESupportTicketStatus.OPEN ? 'Aberto' : 'Concluído'}
                    </Badge>
                  </Flex>

                  <Text variant="caption" mb={1}>
                    <strong>Módulo:</strong> {ticket.module}
                  </Text>

                  <Flex align="center" mt="auto">
                    {ticket.status === ESupportTicketStatus.OPEN && (
                      <Badge
                        colorScheme={ticket.current_assignee === ESupportTicketAssignee.SUPPORT ? 'cyan' : 'purple'}
                        variant="subtle"
                      >
                        {ticket.current_assignee === ESupportTicketAssignee.SUPPORT
                          ? 'Aguardando suporte'
                          : 'Aguardando você'}
                      </Badge>
                    )}
                    <Tooltip label={`Última interação`}>
                      <Text variant="caption" ml="auto">
                        {formatTimeAgo(ticket.updated_at)}
                      </Text>
                    </Tooltip>
                  </Flex>
                </SectionCard>
              ))}
            </SimpleGrid>
          )}
        </Box>
      </SectionCard>

      {canViewFinancialIndicators && (
        <Box mt={4}>
          <FinancialIndicatorsSection />
        </Box>
      )}
    </>
  )
}
